'use client';

import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/common/Button';
import { TrendingUp, Zap } from 'lucide-react';
import type { Bid, Player, Team, Tournament } from '@/lib/types/auction';
import { useAuth } from '@/hooks/useAuth';
// import { useSocket } from '@/hooks/useSocket';
// import { SOCKET_EVENTS } from '@/lib/constants/socket';
import { formatCurrency } from '@/lib/utils/format';
import { logger } from '@/lib/monitoring/logger';

interface BiddingPanelProps {
  currentBid: Bid | null;
  currentPlayer: Player | null;
  hasBid: boolean;
  onPlaceBid: (amount: number) => void;
  disabled?: boolean;
  tournament: Tournament;
}

export function BiddingPanel({
  currentBid,
  currentPlayer,
  hasBid,
  onPlaceBid,
  disabled = false,
  tournament,
}: BiddingPanelProps) {
  const { user } = useAuth();
  // const { socket } = useSocket();
  const isTeamOwner =
    tournament.teams?.some((team: Team) => team.owner?.id === user?.id) ??
    false;

  const currentAmount = hasBid ? (Number(currentBid?.amount) ?? null) : null;
  const basePrice = Number(currentPlayer?.basePrice) || 0;

  const [prevAmount, setPrevAmount] = useState(currentAmount);
  const [bidPlaced, setBidPlaced] = useState(false);
  const [bidSuccess, setBidSuccess] = useState<boolean | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (currentBid?.id) {
      setBidPlaced(false);
      setBidSuccess(null);
      logger.debug(
        'BiddingPanel: Reset bidPlaced state due to new currentBid',
        {
          bidId: currentBid.id,
        }
      );
    }
  }, [currentBid?.id]);

  useEffect(() => {
    if (prevAmount !== null && currentAmount !== prevAmount) {
      setPrevAmount(currentAmount);

      if (bidPlaced) {
        setBidSuccess(true);

        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
          setBidSuccess(null);
          setBidPlaced(false);
          timerRef.current = null;

          logger.debug('BiddingPanel: Reset states after successful bid');
        }, 2000);
      }
    } else if (prevAmount === null && currentAmount !== null) {
      setPrevAmount(currentAmount);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [currentAmount, prevAmount, bidPlaced]);

  useEffect(() => {
    if (bidPlaced) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        setBidPlaced(false);
        setBidSuccess(null);
        timerRef.current = null;

        logger.debug(
          'BiddingPanel: Safety timeout triggered - reset bidPlaced state'
        );
      }, 5000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [bidPlaced]);

  /** ✅ Generate Bid Options */
  const allowedBids = useMemo(() => {
    if (currentAmount === null) {
      return [{ amount: basePrice, increment: 0 }];
    }
    return tournament.allowedBidIncrements
      .map((increment) => ({
        amount: currentAmount + increment.value,
        increment: increment.value,
      }))
      .filter(({ amount }) => amount <= tournament.baseBudget);
  }, [
    currentAmount,
    basePrice,
    tournament.allowedBidIncrements,
    tournament.baseBudget,
  ]);

  // const handleOptOut = useCallback(() => {
  //   if (!socket || !user || !currentPlayer?.id) return;
  //   socket.emit(SOCKET_EVENTS.TEAM_OPTED_OUT, {
  //     teamId: user.id,
  //     playerId: currentPlayer.id,
  //     tournamentId: tournament.id,
  //     timestamp: new Date(),
  //   });
  // }, [socket, user, currentPlayer, tournament]);

  const handlePlaceBid = useCallback(
    async (amount: number) => {
      if (bidPlaced) return;

      setBidPlaced(true);
      setBidSuccess(null);

      try {
        logger.debug('BiddingPanel: Placing bid', { amount });

        onPlaceBid(amount);

        // Only set success if the bid was actually placed
        setBidSuccess(true);
        setTimeout(() => {
          setBidSuccess(null);
          setBidPlaced(false);
        }, 2000);
      } catch (error) {
        setBidSuccess(false);
        setBidPlaced(false);
        // Error toast is already shown by useAuction hook
      }
    },
    [onPlaceBid, bidPlaced]
  );

  return (
    <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
      <AnimatePresence>
        {bidSuccess !== null && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, maxHeight: 0, marginBottom: 0 }}
            animate={{ opacity: 1, scale: 1, maxHeight: 200, marginBottom: 12 }}
            exit={{ opacity: 0, scale: 0.95, maxHeight: 0, marginBottom: 0 }}
            transition={{
              opacity: { duration: 0.25, ease: 'easeOut' },
              scale: { duration: 0.2, ease: 'easeOut' },
              maxHeight: { duration: 0.3, ease: 'easeInOut' },
            }}
            className={`overflow-hidden rounded-lg p-2 text-center text-sm font-medium ${
              bidSuccess
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            <motion.span
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              {bidSuccess
                ? 'Bid placed successfully!'
                : 'Bid failed to process. Please try again.'}
            </motion.span>
          </motion.div>
        )}
      </AnimatePresence>

      {currentAmount === null && isTeamOwner && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex flex-1 items-center justify-center"
        >
          <Button
            onClick={() => handlePlaceBid(basePrice)}
            disabled={disabled || bidPlaced}
            isLoading={bidPlaced && bidSuccess === null}
            className="w-auto bg-gradient-to-r from-yellow-500 to-yellow-600 px-6 py-3 text-lg font-bold text-white shadow-lg transition hover:shadow-xl"
            rightIcon={<Zap className="h-4 w-4" />}
          >
            Start Bidding at {formatCurrency(basePrice)}
          </Button>
        </motion.div>
      )}

      {currentAmount !== null && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="grid flex-1 grid-cols-1 gap-1 sm:grid-cols-1 sm:gap-1"
        >
          {allowedBids.map(({ amount, increment }) => (
            <BidOption
              key={amount}
              amount={amount}
              increment={increment}
              disabled={disabled || bidPlaced}
              isLoading={bidPlaced && bidSuccess === null}
              onPlaceBid={handlePlaceBid}
            />
          ))}
        </motion.div>
      )}

      {/* {isTeamOwner && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-6"
        >
          <Button
            variant="light"
            onClick={handleOptOut}
            disabled={disabled || bidPlaced}
            className="mx-0 my-0 w-full"
          >
            Opt Out
          </Button>
        </motion.div>
      )} */}

      {disabled && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-4 text-center text-sm text-gray-500"
        >
          Bidding is currently paused
        </motion.p>
      )}
    </div>
  );
}

interface BidOptionProps {
  amount: number;
  increment: number;
  disabled: boolean;
  isLoading?: boolean;
  onPlaceBid: (amount: number) => void;
}

const BidOption = ({
  amount,
  increment,
  disabled,
  isLoading = false,
  onPlaceBid,
}: BidOptionProps) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.98 }}
    className="group relative flex items-center justify-center overflow-hidden rounded-lg bg-white py-2"
  >
    <Button
      onClick={() => onPlaceBid(increment)}
      disabled={disabled}
      isLoading={isLoading}
      className="flex h-full w-full flex-col items-center justify-center rounded-lg bg-gradient-to-br from-primary-500 via-accent-blue to-accent-purple p-2 text-white transition hover:shadow-xl disabled:opacity-50 sm:p-3"
    >
      {increment > 0 && (
        <div className="flex items-center space-x-1 text-sm text-white/80">
          <TrendingUp className="h-4 w-4" />
          <span className="text-lg font-bold sm:text-xl">
            +{formatCurrency(increment)}
          </span>
        </div>
      )}
      <span className="text-sm">{formatCurrency(amount)}</span>
    </Button>
  </motion.div>
);
